import React, { useEffect, useState } from "react";
import { Link } from 'react-router-dom';
import { toast } from "sonner";
import { Save, RotateCcw, Clock, Repeat, MessageSquare, Mail, MessageCircle } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { Skeleton, ErrorState } from "@/components/BookingShell";

const CATEGORY_LABELS = {
    "anti-wrinkle":       "Anti Wrinkle",
    "dermal-fillers":     "Dermal Fillers",
    "skin-boosters":      "Skin Boosters",
    "polynucleotides":    "Polynucleotides",
    "fat-dissolving":     "Fat Dissolving",
    "facials":            "Facials / Dermaplaning",
    "microneedling":      "Microneedling / Mesotherapy",
    "vitamin-injections": "Vitamin Injections",
    "hair-filler":        "Hair Filler",
};

// Single source of truth for WhatsApp channel state. Reads the redacted
// backend status from /api/staff/whatsapp/status and translates it into
// UI-safe copy (never leaks WABA ids, template names, or Meta jargon).
function waConnected(waStatus) {
    if (!waStatus) return false;
    return waStatus.connection_state === "connected";
}

function waChannelNote(waStatus) {
    if (!waStatus) return "Checking connection…";
    switch (waStatus.connection_state) {
        case "connected":            return "Ready";
        case "missing_keys":         return "Not connected yet";
        case "missing_verify_token": return "Not connected yet";
        case "disabled":             return "Turned off";
        default:                     return "Not connected yet";
    }
}

export function AutomationSettings() {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [smtp, setSmtp] = useState(null);
    const [waStatus, setWaStatus] = useState(null);

    const load = () => {
        setError(null);
        Promise.all([
            staffApi.getAutomationSettings(),
            staffApi.smtpStatus().catch(() => null),
            staffApi.whatsappStatus().catch(() => null),
        ]).then(([s, sm, wa]) => { setData(s); setSmtp(sm); setWaStatus(wa); }).catch(setError);
    };
    useEffect(load, []);

    if (error) return <ErrorState error={error} onRetry={load} />;
    if (!data)  return <Skeleton className="h-64" />;

    const setAftercare = (v) => setData({ ...data, aftercare_delay_minutes: Math.max(0, Number(v || 0)) });
    const setReview = (v) => setData({ ...data, review_delay_minutes: Math.max(0, Number(v || 0)) });
    const setReviewCat = (cat, mins) => setData({ ...data, review_delay_by_category: { ...data.review_delay_by_category, [cat]: Math.max(0, Number(mins || 0)) } });
    const setRebookCat = (cat, days) => setData({ ...data, rebooking_delay_days_by_category: { ...data.rebooking_delay_days_by_category, [cat]: Math.max(0, Number(days || 0)) } });
    const setChannel = (ch, enabled) => setData({ ...data, channels: { ...data.channels, [ch]: { ...(data.channels?.[ch] || {}), enabled } } });

    const save = async () => {
        setSaving(true);
        try {
            await staffApi.updateAutomationSettings(data);
            toast.success("Automation settings saved.");
        } catch (e) { toast.error(e.message || "Save failed."); }
        finally { setSaving(false); }
    };

    return (
        <div className="space-y-6 md:space-y-8" data-testid="automation-settings">
            <header>
                <div className="text-[10px] uppercase tracking-[0.28em] text-gold/85">Settings</div>
                <h1 className="font-serif text-3xl md:text-4xl text-white">Automation timings</h1>
                <p className="text-sm text-white/50 mt-1">Configure when aftercare, reviews and rebooking reminders go out. Changes apply to new appointments — existing scheduled messages are not moved.</p>
            </header>

            {/* SMTP status */}
            <section className="rounded-2xl glass p-4 md:p-5 space-y-2" data-testid="smtp-status">
                <div className="text-[10px] uppercase tracking-[0.24em] text-white/45">Email delivery (SMTP)</div>
                {smtp ? (
                    <>
                        <div className="text-sm text-white">
                            <span className="font-serif text-lg">{smtp.from_name}</span>{" "}
                            <span className="text-white/60">&lt;{smtp.from_email || "not set"}&gt;</span>
                        </div>
                        <div className="text-xs text-white/50">
                            {smtp.host || "no host"}:{smtp.port} · {smtp.configured ? <span className="text-emerald-200">Ready</span> : <span className="text-amber-200">SMTP password not set</span>}
                        </div>
                        {!smtp.configured && (
                            <div className="text-xs text-amber-200/80 mt-2">
                                Add <code className="text-gold">SMTP_PASSWORD</code> in the backend environment (Gmail: use an App Password) and restart the backend to enable real dispatch.
                            </div>
                        )}
                    </>
                ) : <div className="text-xs text-white/50">SMTP status unavailable.</div>}
            </section>

            {/* Channels */}
            <section className="rounded-2xl glass p-4 md:p-5 space-y-3" data-testid="channels-config">
                <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-gold" />
                    <h2 className="font-serif text-xl text-white">Delivery channels</h2>
                </div>
                <p className="text-xs text-white/50">Automations can be delivered via multiple channels. Only enabled channels are used.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
                    <ChannelRow icon={Mail} label="Email" enabled={!!data.channels?.email?.enabled} onToggle={(v) => setChannel("email", v)} testId="ch-email" note={smtp?.configured ? "SMTP ready" : "SMTP not configured"} />
                    <ChannelRow
                        icon={MessageCircle}
                        label="WhatsApp"
                        enabled={!!data.channels?.whatsapp?.enabled}
                        onToggle={(v) => setChannel("whatsapp", v)}
                        testId="ch-whatsapp"
                        disabled={!waConnected(waStatus)}
                        note={waChannelNote(waStatus)}
                    />
                    <ChannelRow icon={MessageSquare} label="SMS" enabled={!!data.channels?.sms?.enabled} onToggle={(v) => setChannel("sms", v)} testId="ch-sms" disabled note="Not available yet" />
                </div>
            </section>

            {/* Aftercare delay */}
            <section className="rounded-2xl glass p-4 md:p-5 space-y-3" data-testid="aftercare-config">
                <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /><h2 className="font-serif text-xl text-white">Aftercare</h2></div>
                <p className="text-xs text-white/50">How soon after a completed checkout should treatment aftercare emails send.</p>
                <MinutesInput value={data.aftercare_delay_minutes} onChange={setAftercare} testId="aftercare-delay-minutes" />
            </section>

            {/* Review delay */}
            <section className="rounded-2xl glass p-4 md:p-5 space-y-3" data-testid="review-config">
                <div className="flex items-center gap-2"><Clock className="h-4 w-4 text-gold" /><h2 className="font-serif text-xl text-white">Review request</h2></div>
                <p className="text-xs text-white/50">Default timing, plus per-category overrides.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1">Default</div>
                        <MinutesInput value={data.review_delay_minutes} onChange={setReview} testId="review-delay-default" />
                    </div>
                    {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
                        <div key={slug}>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1">{label}</div>
                            <MinutesInput value={data.review_delay_by_category?.[slug] ?? data.review_delay_minutes}
                                          onChange={(v) => setReviewCat(slug, v)}
                                          testId={`review-delay-${slug}`} />
                        </div>
                    ))}
                </div>
            </section>

            {/* Rebooking */}
            <section className="rounded-2xl glass p-4 md:p-5 space-y-3" data-testid="rebooking-config">
                <div className="flex items-center gap-2"><Repeat className="h-4 w-4 text-gold" /><h2 className="font-serif text-xl text-white">Rebooking reminders</h2></div>
                <p className="text-xs text-white/50">Days after checkout to remind the customer to rebook (per treatment category). These timings do not switch reminders on or off.</p>
                <Link to="/staff/communications/automations" className="text-gold underline text-sm">Check rebooking reminder status or switch on/off</Link>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {Object.entries(CATEGORY_LABELS).map(([slug, label]) => (
                        <div key={slug}>
                            <div className="text-[10px] uppercase tracking-[0.18em] text-white/45 mb-1">{label}</div>
                            <div className="flex items-center gap-2">
                                <input type="number" min={1} value={data.rebooking_delay_days_by_category?.[slug] ?? ""}
                                       onChange={(e) => setRebookCat(slug, e.target.value)}
                                       data-testid={`rebook-${slug}`}
                                       className="w-24 bg-transparent border-b border-white/25 text-white outline-none focus:border-gold py-1" />
                                <span className="text-xs text-white/50">days</span>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            <div className="flex items-center justify-end gap-3 pt-2">
                <button type="button" onClick={load} className="text-white/60 text-sm inline-flex items-center gap-1">
                    <RotateCcw className="h-3.5 w-3.5" /> Discard
                </button>
                <button type="button" onClick={save} disabled={saving} data-testid="save-settings"
                        className="rounded-full bg-gold text-navy px-6 h-10 font-semibold text-sm disabled:opacity-50 flex items-center gap-2">
                    <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
                </button>
            </div>
        </div>
    );
}

function MinutesInput({ value, onChange, testId }) {
    // Show a friendly two-field editor: [ value ] × [ unit ]
    // We store minutes internally; unit picker just scales.
    const [unit, setUnit] = useState(() => {
        const v = Number(value || 0);
        if (v === 0) return "minutes";
        if (v % (60 * 24) === 0) return "days";
        if (v % 60 === 0) return "hours";
        return "minutes";
    });
    const scale = { minutes: 1, hours: 60, days: 60 * 24 }[unit];
    const displayed = Math.round((Number(value || 0)) / scale);
    return (
        <div className="flex items-center gap-2">
            <input type="number" min={0} value={displayed}
                   onChange={(e) => onChange(Number(e.target.value || 0) * scale)}
                   data-testid={testId}
                   className="w-24 bg-transparent border-b border-white/25 text-white outline-none focus:border-gold py-1" />
            <select value={unit} onChange={(e) => setUnit(e.target.value)}
                    className="bg-transparent text-white/70 text-sm border-b border-white/25 focus:border-gold py-1">
                <option value="minutes" className="bg-navy">min</option>
                <option value="hours" className="bg-navy">hours</option>
                <option value="days" className="bg-navy">days</option>
            </select>
        </div>
    );
}

function ChannelRow({ icon: Icon, label, enabled, onToggle, testId, disabled, note }) {
    return (
        <label className={`rounded-2xl border p-3 flex items-center gap-3 ${enabled ? "border-gold/40 bg-gold/5" : "border-white/10 bg-white/[0.02]"} ${disabled ? "opacity-60" : ""}`} data-testid={testId}>
            <Icon className={`h-5 w-5 ${enabled ? "text-gold" : "text-white/50"}`} strokeWidth={1.5} />
            <div className="flex-1">
                <div className="text-sm text-white">{label}</div>
                <div className="text-[11px] text-white/50">{note}</div>
            </div>
            <input type="checkbox" checked={!!enabled} onChange={(e) => onToggle(e.target.checked)} disabled={disabled} data-testid={`${testId}-toggle`} />
        </label>
    );
}
